import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PersonalData } from "../../../../types/bookingCreation";
import { RootState } from "../../../../redux/store";
import {
  emailChanged,
  firstNameChanged,
  lastNameChanged,
  setVoucherAmount,
  setVoucherCustomAmount,
  setVoucherCustomAmountIsValid,
  setVoucherSelectedAmount,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { Input } from "components/ui/input";
import { useEmailFieldFeedback } from "../../common/hooks/useEmailFieldFeedback";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

function VoucherOrderDataStep() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const personalData: PersonalData = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );
  const { voucherSelectedAmount, voucherCustomAmount } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );

  const amountData = ["10", "25", "50"];

  const [customAmountError, setCustomAmountError] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const emailFeedback = useEmailFieldFeedback(personalData.email, emailTouched);

  const onFirstNameChange = (firstName: string) =>
    dispatch(firstNameChanged({ firstName }));
  const onLastNameChange = (lastName: string) =>
    dispatch(lastNameChanged({ lastName }));
  const onEmailChange = (email: string) => {
    dispatch(emailChanged({ email }));
  };

  const onCustomAmountChange = (value: string) => {
    if (value === "" || /^[0-9]*$/.test(value)) {
      dispatch(setVoucherCustomAmount(value));
      const num = Number(value);
      // The 5 € floor is enforced by the API too (GIFT_VOUCHER_MIN_AMOUNT in
      // backend/src/core/modules/common/payment/payment.types.ts) — change both.
      const isValid = value !== "" && !isNaN(num) && num >= 5 && num <= 200;
      setCustomAmountError(value !== "" && !isValid);
      dispatch(setVoucherCustomAmountIsValid(isValid));
    }
  };

  //Hook to get the right voucher amount base on the selection user makes
  useEffect(() => {
    if (voucherSelectedAmount === t("general.voucher_purchase.custom_amount")) {
      dispatch(setVoucherAmount(Number(voucherCustomAmount)));
    } else {
      dispatch(setVoucherAmount(Number(voucherSelectedAmount)));
    }
  }, [voucherCustomAmount, voucherSelectedAmount, t, dispatch]);

  return (
    <div>
      <div className="w-full flex flex-col mt-3">
        <div className="space-y-1">
          <label htmlFor="voucher-amount" className="text-sm font-medium">
            {t("general.voucher_purchase.amount_select")}
          </label>
          <Select
            value={voucherSelectedAmount ?? ""}
            onValueChange={(value: string) =>
              dispatch(setVoucherSelectedAmount(value))
            }
          >
            <SelectTrigger id="voucher-amount" className="border-black rounded-xl h-14">
              <SelectValue
                placeholder={t("general.voucher_purchase.amount_select")}
              />
            </SelectTrigger>
            <SelectContent>
              {amountData.map((quantity) => (
                <SelectItem key={quantity} value={quantity}>
                  {tenantConfig.currencySymbol}
                  {quantity}
                </SelectItem>
              ))}
              <SelectItem
                value={t("general.voucher_purchase.custom_amount")}
              >
                {t("general.voucher_purchase.custom_amount")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {voucherSelectedAmount ===
        t("general.voucher_purchase.custom_amount") && (
        <div className="space-y-1 my-4">
          <label
            htmlFor="voucher-custom-amount"
            className="text-sm font-medium"
          >
            {`${t("general.voucher_purchase.custom_amount_label")} (${tenantConfig.currencySymbol})`}
            <span aria-hidden="true" className="text-appRed ml-0.5">
              *
            </span>
          </label>
          <Input
            id="voucher-custom-amount"
            value={voucherCustomAmount}
            onChange={(e) => onCustomAmountChange(e.target.value)}
            placeholder="5 - 200"
            inputMode="numeric"
            pattern="[0-9]*"
            required
            error={customAmountError}
            aria-required="true"
            aria-invalid={customAmountError}
            aria-describedby={customAmountError ? "voucher-custom-amount-error" : undefined}
          />
          {customAmountError && (
            <p id="voucher-custom-amount-error" className="text-sm text-appRed mt-1">
              {t("general.voucher_purchase.custom_amount_error", {
                defaultValue: "Please enter a number between 5 and 200",
              })}
            </p>
          )}
        </div>
      )}

      <div className="my-4">
        <b className="font-semibold text-foreground">{`${t(
          "general.voucher_purchase.personal_data"
        )}`}</b>
      </div>

      <div className="space-y-1 mb-3">
        <label htmlFor="voucher-first-name" className="text-sm font-medium">
          {t("book_location.personal_data.first_name")}
          <span aria-hidden="true" className="text-appRed ml-0.5">
            *
          </span>
        </label>
        <Input
          id="voucher-first-name"
          value={personalData.firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          placeholder={t("book_location.personal_data.first_name")}
          autoComplete="given-name"
          required
          aria-required="true"
        />
      </div>

      <div className="space-y-1 mb-3">
        <label htmlFor="voucher-last-name" className="text-sm font-medium">
          {t("book_location.personal_data.last_name")}
          <span aria-hidden="true" className="text-appRed ml-0.5">
            *
          </span>
        </label>
        <Input
          id="voucher-last-name"
          value={personalData.lastName}
          onChange={(e) => onLastNameChange(e.target.value)}
          placeholder={t("book_location.personal_data.last_name")}
          autoComplete="family-name"
          required
          aria-required="true"
        />
      </div>

      <div className="space-y-1 mb-3">
        <label htmlFor="voucher-email" className="text-sm font-medium">
          {t("book_location.personal_data.email")}
          <span aria-hidden="true" className="text-appRed ml-0.5">
            *
          </span>
        </label>
        <Input
          id="voucher-email"
          value={personalData.email}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={() => setEmailTouched(true)}
          placeholder={t("book_location.personal_data.email")}
          autoComplete="email"
          type="email"
          required
          error={!!emailFeedback.error}
          aria-required="true"
          aria-invalid={!!emailFeedback.error}
          aria-describedby={
            emailFeedback.error
              ? "voucher-email-error"
              : emailFeedback.warning
              ? "voucher-email-warning"
              : undefined
          }
        />
        {emailFeedback.error && (
          <p id="voucher-email-error" className="text-sm text-appRed mt-1">
            {emailFeedback.error}
          </p>
        )}
        {emailFeedback.warning && (
          <p id="voucher-email-warning" className="text-sm text-slate-600 mt-1">
            {emailFeedback.warning}
          </p>
        )}
      </div>

      <div className="bg-[#EDF0F5] text-gray-800 p-2 rounded-lg w-full my-2.5">
        <p className="text-sm">
          <Trans
            i18nKey={"general.voucher_purchase.note"}
            components={{
              bold: <span className="font-bold"></span>,
            }}
          />
        </p>
      </div>
    </div>
  );
}

export default VoucherOrderDataStep;
